import {
  Box,
  makeStyles,
  Menu,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { AccountBox, CheckBoxOutlineBlank, Lock } from '@material-ui/icons'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import DoorFrontIcon from '@mui/icons-material/DoorFront'
import camelcaseKeys from 'camelcase-keys'
import React, { useMemo } from 'react'
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  ListProps,
  Pagination,
  ReferenceField,
  TextField,
  TextInput,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import FilterProps from '../../common/FilterProps'
import QuickFilter from '../../common/QuickFilter'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import {
  CheckIfAnyModuleHasError,
  CheckLastRefreshError,
} from '../common/modules/ModuleStatus'
import { DepositorsAttachEventRecurringBulkButton } from './depositor-show/DepositorAttachEventRecurringBulkButton'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { DepositorDoorStatus } from '../../../core/enum/DepositorDoorStatus'
import { DepositorLockerStatus } from '../../../core/enum/DepositorLockerStatus'
import { useColorPalette } from '../../../core/devices/devices.utils'
import { DeviceStatusCheck } from '../../../core/enum/DeviceStatusCheck'
import { DepositorActionBulkButton } from './depositor-list/DepositorActionBulkButton'

const useStyles = makeStyles((theme) => ({
  compactStatus: {
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 34,
    cursor: 'default',
  },
  statusIcon: {
    fill: theme.palette.text.primary,
  },
}))

const DepositorsFilters = (props: FilterProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Filter {...props}>
      <TextInput source="name" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.palms.fields.categoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      <QuickFilter source="eventActive" defaultValue />
    </Filter>
  )
}

const DepositorBulkActions = ({ ...props }) => {
  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const DepositorOpenLockersAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DepositorActionBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          depositorAction="openLocker"
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const DepositorReleaseLockersAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DepositorActionBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          depositorAction="releaseLocker"
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const DepositorAttachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DepositorsAttachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          eventAction="attach"
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  const DepositorDetachEventRecurringAction = useMemo(
    () =>
      React.forwardRef((ref, forwardRefProps) => (
        <DepositorsAttachEventRecurringBulkButton
          selectedIds={props.selectedIds as number[]}
          innerRef={ref}
          eventAction="detach"
          {...forwardRefProps}
        />
      )),
    [props.selectedIds],
  )

  return (
    <div>
      <Button
        onClick={handleClick}
        label="const.selectAction"
        variant="text"
        color="default"
        useSmallVersionBreakpoint={false}
      />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <DepositorOpenLockersAction />
        <DepositorReleaseLockersAction />
        <DepositorAttachEventRecurringAction />
        <DepositorDetachEventRecurringAction />
      </Menu>
    </div>
  )
}

const CompactStatus = ({ ...props }) => {
  const { record: door } = props
  const classes = useStyles()
  const translate = useTranslate()

  const jsonStatus =
    door?.jsonStatus &&
    camelcaseKeys(JSON.parse(door?.jsonStatus), { deep: true })[0]

  const getDoorStatusTooltip = () => {
    if (jsonStatus?.doorStatus === DepositorDoorStatus.opened)
      return translate('resources.enums.DepositorDoorStatus.opened')
    if (jsonStatus?.doorStatus === DepositorDoorStatus.closed)
      return translate('resources.enums.DepositorDoorStatus.closed')
    if (jsonStatus?.doorStatus === DepositorDoorStatus.locked)
      return translate('resources.enums.DepositorDoorStatus.locked')
    return ''
  }

  const getLockerStatusTooltip = () => {
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.empty)
      return translate('resources.enums.DepositorLockerStatus.empty')
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.full)
      return translate('resources.enums.DepositorLockerStatus.full')
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.reserved)
      return translate('resources.enums.DepositorLockerStatus.reserved')
    return ''
  }

  const DoorStatusIcon = () => {
    if (jsonStatus?.doorStatus === DepositorDoorStatus.opened)
      return <MeetingRoomIcon />
    if (jsonStatus?.doorStatus === DepositorDoorStatus.closed)
      return <DoorFrontIcon />
    if (jsonStatus?.doorStatus === DepositorDoorStatus.locked) return <Lock />
    return <div />
  }

  const LockerStatusIcon = () => {
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.empty)
      return <CheckBoxOutlineBlank />
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.full)
      return <CheckBoxIcon />
    if (jsonStatus?.lockerStatus === DepositorLockerStatus.reserved)
      return <AccountBox />
    return <div />
  }

  return (
    <Box
      className={classes.compactStatus}
      onClick={(event) => event.stopPropagation()}
    >
      {jsonStatus && (
        <Tooltip title={getDoorStatusTooltip()}>
          <div>
            <DoorStatusIcon />
          </div>
        </Tooltip>
      )}
      {jsonStatus && (
        <Tooltip title={getLockerStatusTooltip()}>
          <div>
            <LockerStatusIcon />
          </div>
        </Tooltip>
      )}
    </Box>
  )
}

export const DepositorsList = ({ ...props }: ListProps) => {
  const theme = useTheme()
  const hasAuthority = useHasAuthority()
  const colorPalette = useColorPalette(theme)

  const { data: serverDate } = useQuery({
    type: sanitizeFetchType('getServerDateTime'),
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const postRowStyle = (record: any) => {
    if (!serverDate) return {}

    const depositorError = CheckLastRefreshError(
      record?.lastRefresh,
      serverDate?.serverDateTime,
    )
    if (depositorError) return colorPalette(DeviceStatusCheck.ERROR)

    const depositorWarning = CheckIfAnyModuleHasError(
      record.jsonStatus,
      DeviceTypes.DEPOSITOR,
      serverDate?.serverDateTime,
    )
    if (depositorWarning) return colorPalette(DeviceStatusCheck.WARNING)
    return colorPalette(DeviceStatusCheck.SUCCESS)
  }

  return (
    <List
      {...props}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      exporter={false}
      filters={<DepositorsFilters />}
      bulkActionButtons={<DepositorBulkActions />}
    >
      <Datagrid
        rowStyle={postRowStyle}
        size="small"
        rowClick={(id) => `${ResourceName.DEPOSITORS}/${id}/show`}
        isRowExpandable={(record) =>
          record?.jsonStatus !== undefined && record?.jsonStatus !== null
        }
      >
        <TextField source="id" label="ID" />
        <TextField source="name" />
        {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link={false}
            sortBy="name"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        <CompactStatus source="status" sortable={false} />
      </Datagrid>
    </List>
  )
}
